import { useState, useRef } from 'react'
import { X, Plus, FileText, HelpCircle, Image, Video, Upload, Info, Loader2 } from 'lucide-react'
import { useCreateWaTemplate } from '@hooks/useWaTemplates'
import { toast } from 'react-hot-toast'
import { WaTemplateType } from '@/types'
import { api } from '@services/api'

interface CreateWaTemplateDialogProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface VariableInput {
  name: string
  defaultValue: string
}

interface TemplateFormData {
  name: string
  content: string
  type: WaTemplateType
  mediaUrl: string
  variables: VariableInput[]
}

export function CreateWaTemplateDialog({ isOpen, onClose, onSuccess }: CreateWaTemplateDialogProps) {
  const [formData, setFormData] = useState<TemplateFormData>({
    name: '',
    content: '',
    type: 'TEXT',
    mediaUrl: '',
    variables: [],
  })
  const [newVariable, setNewVariable] = useState({ name: '', defaultValue: '' })
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [filePreview, setFilePreview] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [showVariableHelp, setShowVariableHelp] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const createTemplate = useCreateWaTemplate()

  const handleClose = () => {
    setFormData({
      name: '',
      content: '',
      type: 'TEXT',
      mediaUrl: '',
      variables: [],
    })
    setNewVariable({ name: '', defaultValue: '' })
    clearSelectedFile()
    setShowVariableHelp(false)
    onClose()
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const allowedTypes: Record<string, string[]> = {
      IMAGE: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      VIDEO: ['video/mp4', 'video/quicktime', 'video/webm'],
      DOCUMENT: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/plain'],
    }

    const allowed = allowedTypes[formData.type] || []
    if (!allowed.some(type => file.type.startsWith(type.split('/')[0]) || file.type === type)) {
      toast.error(`Please select a valid ${formData.type.toLowerCase()} file`)
      return
    }

    if (file.size > 16 * 1024 * 1024) {
      toast.error('File size must be less than 16MB')
      return
    }

    setSelectedFile(file)

    if (file.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onload = (event) => {
        setFilePreview(event.target?.result as string)
      }
      reader.readAsDataURL(file)
    } else {
      setFilePreview(null)
    }
  }

  const clearSelectedFile = () => {
    setSelectedFile(null)
    setFilePreview(null)
    setFormData(prev => ({ ...prev, mediaUrl: '' }))
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const uploadFile = async (): Promise<string | null> => {
    if (!selectedFile) return null

    setIsUploading(true)
    try {
      const uploadFormData = new FormData()
      uploadFormData.append('file', selectedFile)

      const response = await api.post('/whatsapp/messages/upload-template-media', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      return response.data?.data?.mediaUrl || null
    } catch (error) {
      console.error('Upload failed:', error)
      toast.error('Failed to upload file')
      return null
    } finally {
      setIsUploading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      toast.error('Template name is required')
      return
    }

    if (!formData.content.trim()) {
      toast.error('Template content is required')
      return
    }

    const isMediaType = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(formData.type)
    if (isMediaType && !formData.mediaUrl.trim() && !selectedFile) {
      toast.error(`Please upload a file or provide a URL for ${formData.type.toLowerCase()} templates`)
      return
    }

    try {
      let mediaUrl = formData.mediaUrl.trim()

      if (selectedFile && isMediaType) {
        const uploadedUrl = await uploadFile()
        if (!uploadedUrl) {
          return
        }
        mediaUrl = uploadedUrl
      }

      await createTemplate.mutateAsync({
        name: formData.name.trim(),
        content: formData.content.trim(),
        type: formData.type,
        mediaUrl: mediaUrl || undefined,
        variables: formData.variables.length > 0
          ? formData.variables.map(v => ({ name: v.name, defaultValue: v.defaultValue || undefined }))
          : undefined,
      })

      toast.success('Template created successfully')
      handleClose()
      onSuccess?.()
    } catch (error: any) {
      toast.error(error?.message || 'Failed to create template')
    }
  }

  const addVariable = () => {
    if (!newVariable.name.trim()) {
      toast.error('Variable name is required')
      return
    }
    if (formData.variables.some(v => v.name === newVariable.name.trim())) {
      toast.error('Variable already exists')
      return
    }
    setFormData((prev) => ({
      ...prev,
      variables: [...prev.variables, { name: newVariable.name.trim(), defaultValue: newVariable.defaultValue.trim() }],
    }))
    setNewVariable({ name: '', defaultValue: '' })
  }

  const removeVariable = (name: string) => {
    setFormData((prev) => ({
      ...prev,
      variables: prev.variables.filter((v) => v.name !== name),
    }))
  }

  const insertVariable = (variableName: string) => {
    const placeholder = `{{${variableName}}}`
    setFormData((prev) => ({
      ...prev,
      content: prev.content + placeholder,
    }))
  }

  const getAcceptTypes = () => {
    switch (formData.type) {
      case 'IMAGE':
        return 'image/jpeg,image/png,image/gif,image/webp'
      case 'VIDEO':
        return 'video/mp4,video/quicktime,video/webm'
      case 'DOCUMENT':
        return '.pdf,.doc,.docx,.xls,.xlsx,.txt'
      default:
        return ''
    }
  }

  const getMediaIcon = () => {
    switch (formData.type) {
      case 'IMAGE':
        return <Image size={24} className="text-gray-500" />
      case 'VIDEO':
        return <Video size={24} className="text-gray-500" />
      default:
        return <FileText size={24} className="text-gray-500" />
    }
  }

  if (!isOpen) return null

  const isMediaType = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(formData.type)
  const isPending = createTemplate.isPending || isUploading

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Create Message Template</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Template Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Welcome Message"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Type</label>
            <select
              value={formData.type}
              onChange={(e) => {
                setFormData({ ...formData, type: e.target.value as TemplateFormData['type'], mediaUrl: '' })
                clearSelectedFile()
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="TEXT">Text</option>
              <option value="IMAGE">Image</option>
              <option value="VIDEO">Video</option>
              <option value="DOCUMENT">Document</option>
              <option value="INTERACTIVE">Interactive</option>
              <option value="QUICK_REPLY">Quick Reply</option>
            </select>
          </div>

          {isMediaType && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Media Attachment <span className="text-red-500">*</span>
              </label>

              {selectedFile ? (
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    {filePreview ? (
                      <img src={filePreview} alt="Preview" className="w-16 h-16 object-cover rounded-lg" />
                    ) : (
                      <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                        {getMediaIcon()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button type="button" onClick={clearSelectedFile} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full" title="Remove file">
                      <X size={18} />
                    </button>
                  </div>
                </div>
              ) : (
                <div onClick={() => fileInputRef.current?.click()} className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-green-400 hover:bg-green-50/50 transition-colors">
                  <div className="flex flex-col items-center gap-2">
                    <div className="p-3 bg-gray-100 rounded-full">
                      <Upload size={24} className="text-gray-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Click to upload {formData.type.toLowerCase()}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.type === 'IMAGE' && 'JPG, PNG, GIF, WEBP up to 16MB'}
                        {formData.type === 'VIDEO' && 'MP4, MOV, WEBM up to 16MB'}
                        {formData.type === 'DOCUMENT' && 'PDF, DOC, DOCX, XLS, XLSX, TXT up to 16MB'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept={getAcceptTypes()} />

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">or enter URL</span>
                </div>
              </div>

              <input
                type="url"
                value={formData.mediaUrl}
                onChange={(e) => {
                  setFormData({ ...formData, mediaUrl: e.target.value })
                  if (e.target.value) clearSelectedFile()
                }}
                placeholder={`https://example.com/media.${formData.type === 'IMAGE' ? 'jpg' : formData.type === 'VIDEO' ? 'mp4' : 'pdf'}`}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                disabled={!!selectedFile}
              />
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="block text-sm font-medium text-gray-700">Variables (Optional)</label>
              <button type="button" onClick={() => setShowVariableHelp(!showVariableHelp)} className="text-gray-400 hover:text-gray-600" title="How to use variables">
                <HelpCircle size={16} />
              </button>
            </div>

            {showVariableHelp && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <div className="flex gap-2">
                  <Info size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="text-blue-700 space-y-3">
                    <div>
                      <p className="font-medium mb-1">Built-in Variables (auto-filled from contact):</p>
                      <div className="grid grid-cols-1 gap-1 text-xs">
                        <div className="flex items-start gap-2">
                          <code className="bg-blue-100 px-1.5 py-0.5 rounded whitespace-nowrap">{'{{name}}'}</code>
                          <span>First name only (e.g., "John" from "John Smith")</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <code className="bg-blue-100 px-1.5 py-0.5 rounded whitespace-nowrap">{'{{firstName}}'}</code>
                          <span>Same as name - first name only</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <code className="bg-blue-100 px-1.5 py-0.5 rounded whitespace-nowrap">{'{{fullName}}'}</code>
                          <span>Complete name (e.g., "John Smith")</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <code className="bg-blue-100 px-1.5 py-0.5 rounded whitespace-nowrap">{'{{phone}}'}</code>
                          <span>Contact's phone number</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <p className="font-medium mb-1">Custom Variables:</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>Create your own variables with default values below</li>
                        <li>Click the variable chip to insert it into your message</li>
                        <li>Default value is used if contact data is not available</li>
                      </ul>
                    </div>
                    <p className="text-xs border-t border-blue-200 pt-2">
                      <strong>Example:</strong> "Hi {'{{name}}'}, how are you?" becomes "Hi John, how are you!"
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <input
                type="text"
                value={newVariable.name}
                onChange={(e) => setNewVariable({ ...newVariable, name: e.target.value.replace(/\s/g, '_') })}
                placeholder="Variable name"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <input
                type="text"
                value={newVariable.defaultValue}
                onChange={(e) => setNewVariable({ ...newVariable, defaultValue: e.target.value })}
                placeholder="Default value"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <button type="button" onClick={addVariable} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                <Plus size={20} />
              </button>
            </div>

            {formData.variables.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.variables.map((variable) => (
                  <span
                    key={variable.name}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm cursor-pointer hover:bg-blue-200"
                    onClick={() => insertVariable(variable.name)}
                    title="Click to insert in content"
                  >
                    {`{{${variable.name}}}`}
                    {variable.defaultValue && <span className="text-blue-600">= {variable.defaultValue}</span>}
                    <button type="button" onClick={(e) => { e.stopPropagation(); removeVariable(variable.name) }} className="text-blue-600 hover:text-blue-800 ml-1">
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-500">Click a variable to insert it into the content</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {isMediaType ? 'Caption' : 'Message Content'} <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder={isMediaType ? 'Add a caption with your media...' : 'Hello {{name}}, welcome to our service!'}
              rows={5}
              maxLength={10000}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
              required
            />
            <div className="text-xs text-gray-400 text-right">{formData.content.length}/10000</div>
          </div>
        </form>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3 bg-gray-50">
          <button type="button" onClick={handleClose} className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors" disabled={isPending}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{isUploading ? 'Uploading...' : 'Creating...'}</span>
              </>
            ) : (
              <>
                <FileText className="w-4 h-4" />
                <span>Create Template</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
